import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import '../Auth.css';

const AdminLogin: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAdmin, isAuthenticated, loading: authLoading, user } = useAuth();

  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginAttempted, setLoginAttempted] = useState(false);

  // If already logged in as admin, redirect to admin dashboard
  useEffect(() => {
    if (!authLoading && isAuthenticated && isAdmin) {
      const from = (location.state as any)?.from?.pathname || '/admin/dashboard';
      navigate(from, { replace: true });
    }
  }, [authLoading, isAuthenticated, isAdmin, navigate, location]);

  // After login attempt, check if user is admin
  useEffect(() => {
    if (loginAttempted && !authLoading && user) {
      if (isAdmin) {
        const from = (location.state as any)?.from?.pathname;
        if (from && from.startsWith('/admin')) {
          navigate(from, { replace: true });
        } else {
          navigate('/admin/dashboard', { replace: true });
        }
      } else {
        // Not an admin
        setError('Access denied. Admin privileges required.');
        setLoading(false);
        setLoginAttempted(false);
      }
    }
  }, [loginAttempted, authLoading, user, isAdmin, navigate, location]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(formData);
      setLoginAttempted(true);
    } catch (err: any) {
      console.error('Admin login error:', err);
      if (err.response?.status === 401) {
        setError(t('auth.invalid_credentials') || 'Invalid username or password');
      } else {
        setError(t('auth.login_error') || 'Login failed. Please try again.');
      }
      setLoading(false);
      setLoginAttempted(false);
    }
  };

  if (authLoading) {
    return (
      <div className="auth-page-wrapper">
        <div className="auth-card">
          <div style={{ textAlign: 'center', padding: '2rem' }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page-wrapper" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
      <div className="auth-card" style={{ maxWidth: '400px', width: '100%' }}>
        <h2 className="auth-title" style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          🔐 Admin Login
        </h2>

        {error && (
          <div className="auth-error" style={{ background: '#fee', color: '#c00', padding: '0.75rem', borderRadius: '4px', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group" style={{ marginBottom: '1rem' }}>
            <label htmlFor="username" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Username
            </label>
            <input
              type="text"
              id="username"
              name="username"
              className="input-field"
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '1rem' }}
              value={formData.username}
              onChange={handleChange}
              required
              autoComplete="username"
              disabled={loading}
              placeholder="Enter admin username"
            />
          </div>

          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="password" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              className="input-field"
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '1rem' }}
              value={formData.password}
              onChange={handleChange}
              required
              autoComplete="current-password"
              disabled={loading}
              placeholder="Enter password"
            />
          </div>

          <button
            type="submit"
            className="auth-btn"
            disabled={loading}
            style={{
              width: '100%',
              padding: '0.875rem',
              background: loading ? '#ccc' : '#2e7d32',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Signing in...' : 'Sign In to Admin'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.875rem', color: '#666' }}>
          <a href="/" style={{ color: '#2e7d32', textDecoration: 'none' }}>
            ← Back to Store
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
